/**
 * Production Order Form Page - Multi-BOM Support
 *
 * Create or edit a Production Order with multiple BOM items
 * Matches the data structure created by UnifiedConversionDialog
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../components/ui/sheet';
import {
  ArrowLeft,
  Save,
  Loader2,
  AlertCircle,
  Search,
  Factory,
  FileText,
  Package,
  Calendar,
  Plus,
  Trash2,
} from 'lucide-react';
import Layout from '../components/Layout';
import productionService from '../services/productionService';
import bomService from '../services/bomService';
import productService from '../services/productService';
import ClientSelector from '../components/ClientSelector';
import BomForm from '../components/BomForm';

function ProductionFormPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Multi-item BOM state - matches UnifiedConversionDialog structure
  const [items, setItems] = useState([]);
  
  // Common order fields
  const [productionOrderId, setProductionOrderId] = useState('');
  const [priority, setPriority] = useState('normal');
  const [workCenterId, setWorkCenterId] = useState('');
  const [plannedStartDate, setPlannedStartDate] = useState('');
  const [plannedEndDate, setPlannedEndDate] = useState('');
  const [notes, setNotes] = useState('');

  // Source info (from sales order)
  const [sourceType, setSourceType] = useState('');
  const [sourceId, setSourceId] = useState('');

  // Client selection
  const [clientId, setClientId] = useState('');
  const [clientName, setClientName] = useState('');

  // Work centers
  const [workCenters, setWorkCenters] = useState([]);

  // BOM Search Dialog
  const [bomSearchOpen, setBomSearchOpen] = useState(false);
  const [bomSearchTerm, setBomSearchTerm] = useState('');
  const [bomSearchResults, setBomSearchResults] = useState([]);
  const [bomSearchLoading, setBomSearchLoading] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null);

  // Product Search Dialog
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);

  // BOM Creation Drawer
  const [bomCreateOpen, setBomCreateOpen] = useState(false);

  // Load work centers
  const loadWorkCenters = useCallback(async () => {
    try {
      const response = await productionService.getWorkCenters({ status: 'active' });
      setWorkCenters(response.items || response || []);
    } catch (err) {
      console.error('Failed to load work centers:', err);
    }
  }, []);

  useEffect(() => {
    loadWorkCenters();
  }, [loadWorkCenters]);

  // Load order for editing
  const loadOrder = useCallback(async () => {
    if (!isEditMode) return;

    try {
      setLoading(true);
      setError(null);

      const order = await productionService.getProductionOrder(id);
      
      // Load items (multi-item support)
      if (order.items && order.items.length > 0) {
        const loadedItems = [];
        for (const item of order.items) {
          try {
            const bom = await bomService.getBom(item.bom_id);
            loadedItems.push({
              id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              bomId: item.bom_id,
              selectedBom: bom,
              plannedQuantity: item.planned_quantity || 0,
              lineItemId: item.line_item_id || null
            });
          } catch (err) {
            console.error('Failed to load BOM for item:', item.bom_id, err);
          }
        }
        setItems(loadedItems);
      } else if (order.bom_id) {
        // Legacy single-item order
        try {
          const bom = await bomService.getBom(order.bom_id);
          setItems([{
            id: `item-${Date.now()}`,
            bomId: order.bom_id,
            selectedBom: bom,
            plannedQuantity: order.planned_quantity || 0,
            lineItemId: null
          }]);
        } catch (err) {
          console.error('Failed to load BOM:', err);
        }
      }

      // Load common fields
      setProductionOrderId(order.production_order_id || '');
      setPriority(order.priority || 'normal');
      setWorkCenterId(order.work_center_id || '');
      setPlannedStartDate(order.planned_start_date || '');
      setPlannedEndDate(order.planned_end_date || '');
      setNotes(order.notes || '');
      setSourceType(order.source_type || '');
      setSourceId(order.source_id || '');
      setClientId(order.client_id || '');
      setClientName(order.client_name || '');
    } catch (err) {
      console.error('Failed to load order:', err);
      setError('Failed to load production order. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id, isEditMode]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  // Check for pre-filled BOM from URL params
  useEffect(() => {
    const bomIdParam = searchParams.get('bom_id');
    const qtyParam = searchParams.get('quantity');
    const sourceParam = searchParams.get('source_type');
    const sourceIdParam = searchParams.get('source_id');

    if (bomIdParam && !isEditMode) {
      bomService.getBom(bomIdParam).then((bom) => {
        setItems([{
          id: `item-${Date.now()}`,
          bomId: bomIdParam,
          selectedBom: bom,
          plannedQuantity: Number(qtyParam) || 1,
          lineItemId: null
        }]);
      }).catch((err) => {
        console.error('Failed to load BOM from URL:', err);
      });
    }

    if (sourceParam) setSourceType(sourceParam);
    if (sourceIdParam) setSourceId(sourceIdParam);
  }, [searchParams, isEditMode]);

  // Search BOMs
  const searchBoms = useCallback(async () => {
    try {
      setBomSearchLoading(true);
      const response = await bomService.getBoms({
        search: bomSearchTerm,
        status: 'active',
        page_size: 20,
      });
      setBomSearchResults(response.items || []);
    } catch (err) {
      console.error('Failed to search BOMs:', err);
    } finally {
      setBomSearchLoading(false);
    }
  }, [bomSearchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (bomSearchOpen) {
        searchBoms();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [bomSearchTerm, bomSearchOpen, searchBoms]);

  // Item management functions
  const handleAddItem = () => {
    setEditingItemIndex(null);
    setBomSearchOpen(true);
  };

  const handleEditItem = (index) => {
    setEditingItemIndex(index);
    setBomSearchOpen(true);
  };

  const handleRemoveItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateItemQuantity = (index, quantity) => {
    setItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, plannedQuantity: Number(quantity) || 0 } : item
      )
    );
  };

  const selectBom = (bom) => {
    if (editingItemIndex !== null) {
      // Editing existing item
      setItems((prev) =>
        prev.map((item, i) =>
          i === editingItemIndex
            ? { ...item, bomId: bom._id, selectedBom: bom }
            : item
        )
      );
    } else {
      // Adding new item
      setItems((prev) => [
        ...prev,
        {
          id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          bomId: bom._id,
          selectedBom: bom,
          plannedQuantity: 1,
          lineItemId: null,
        },
      ]);
    }
    setBomSearchOpen(false);
    setEditingItemIndex(null);
  };

  const handleBomCreated = (bom) => {
    setBomCreateOpen(false);
    setItems((prev) => [
      ...prev,
      {
        id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        bomId: bom._id,
        selectedBom: bom,
        plannedQuantity: 1,
        lineItemId: null,
      },
    ]);
  };

  // Product Search functions
  const searchProducts = useCallback(async () => {
    try {
      setProductSearchLoading(true);
      const response = await productService.searchProducts(productSearchTerm, 1, 20);
      setProductSearchResults(response.products || []);
    } catch (err) {
      console.error('Failed to search products:', err);
    } finally {
      setProductSearchLoading(false);
    }
  }, [productSearchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearchOpen) {
        searchProducts();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearchTerm, productSearchOpen, searchProducts]);

  const handleAddProductItem = () => {
    setEditingItemIndex(null);
    setProductSearchOpen(true);
  };

  const selectProduct = (product) => {
    if (editingItemIndex !== null) {
      // Editing existing item
      setItems((prev) =>
        prev.map((item, i) =>
          i === editingItemIndex
            ? { ...item, productId: product._id, selectedProduct: product }
            : item
        )
      );
    } else {
      // Adding new item
      setItems((prev) => [
        ...prev,
        {
          id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          productId: product._id,
          selectedProduct: product,
          plannedQuantity: 1,
          lineItemId: null,
        },
      ]);
    }
    setProductSearchOpen(false);
    setEditingItemIndex(null);
  };

  // Navigation
  const handleBack = () => {
    if (isEditMode) {
      navigate(`/production/${id}`);
    } else {
      navigate('/production');
    }
  };

  // Save handler
  const handleSave = async () => {
    // Validate
    const invalidItem = items.find(
      (item) => !item.plannedQuantity || item.plannedQuantity <= 0
    );
    if (invalidItem) {
      setError('所有项目的计划数量必须大于0。');
      return;
    }

    if (!clientId) {
      setError('请选择客户，以便正确归集库存。');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const orderData = {
        items: items.map((item) => ({
          bom_id: item.bomId || undefined,
          finished_product_id: item.selectedProduct ? item.selectedProduct._id : undefined,
          finished_product_code: item.selectedProduct ? item.selectedProduct.product_id : undefined,
          finished_product_name: item.selectedProduct ? item.selectedProduct.name : undefined,
          planned_quantity: Number(item.plannedQuantity),
          line_item_id: item.lineItemId,
        })),
        production_order_id: productionOrderId || undefined,
        priority,
        client_id: clientId || null,
        client_name: clientName || '',
        work_center_id: workCenterId || null,
        planned_start_date: plannedStartDate || null,
        planned_end_date: plannedEndDate || null,
        notes,
      };

      // Add source info if creating from sales order
      if (!isEditMode && sourceType) {
        orderData.source_type = sourceType;
        orderData.source_id = sourceId;
      }

      if (isEditMode) {
        await productionService.updateProductionOrder(id, orderData);
        navigate(`/production/${id}`);
      } else {
        const newOrder = await productionService.createProductionOrder(orderData);
        navigate(`/production/${newOrder._id}`);
      }
    } catch (err) {
      console.error('Failed to save order:', err);
      setError(`Failed to save production order: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Calculate material requirements for all items
  const materialRequirements = items.flatMap((item) => {
    if (!item.selectedBom || !item.selectedBom.materials) return [];
    return item.selectedBom.materials.map((m) => ({
      ...m,
      required_quantity: (
        m.quantity_per *
        item.plannedQuantity *
        (1 + (m.scrap_rate || 0))
      ).toFixed(3),
      _item_info: {
        product_code: item.selectedBom.finished_product_code,
        product_name: item.selectedBom.finished_product_name,
      },
    }));
  });

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto py-6">
          <div className="flex justify-center items-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto py-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">
                {isEditMode ? '编辑生产工单' : '新建生产工单'}
              </h1>
              <p className="text-muted-foreground">
                {isEditMode ? '更新生产工单详情' : '创建新的生产工单'}
              </p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            保存工单
          </Button>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Source Info */}
        {sourceType && (
          <Alert>
            <FileText className="h-4 w-4" />
            <AlertDescription>
              从{sourceType}创建: {sourceId}
            </AlertDescription>
          </Alert>
        )}

        {/* BOM Items Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              生产项目
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>BOM</TableHead>
                  <TableHead>产品</TableHead>
                  <TableHead className="w-32">计划数量</TableHead>
                  <TableHead className="w-20">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      {item.selectedBom ? (
                        <div className="font-mono text-sm">
                          {item.selectedBom.bom_id}
                        </div>
                      ) : item.selectedProduct ? (
                        <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                          待补BOM
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.selectedBom ? (
                        <div>
                          <div className="font-medium">
                            {item.selectedBom.finished_product_code}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {item.selectedBom.finished_product_name}
                          </div>
                        </div>
                      ) : item.selectedProduct ? (
                        <div>
                          <div className="font-medium">
                            {item.selectedProduct.product_id}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {item.selectedProduct.name}
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={item.plannedQuantity}
                        onChange={(e) =>
                          handleUpdateItemQuantity(index, e.target.value)
                        }
                        min={1}
                        disabled={isEditMode}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditItem(index)}
                          disabled={isEditMode}
                        >
                          <Search className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveItem(index)}
                          disabled={isEditMode}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="text-center text-muted-foreground py-8"
                    >
                      暂无生产项目（可直接添加产品，BOM后续补充）
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleAddProductItem}
                disabled={isEditMode}
                className="flex-1"
              >
                <Package className="h-4 w-4 mr-2" />
                选择产品
              </Button>
              <Button
                variant="outline"
                onClick={handleAddItem}
                disabled={isEditMode}
                className="flex-1"
              >
                <Search className="h-4 w-4 mr-2" />
                选择BOM
              </Button>
              <Button
                variant="outline"
                onClick={() => setBomCreateOpen(true)}
                disabled={isEditMode}
              >
                <Plus className="h-4 w-4 mr-2" />
                创建BOM
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Order Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Factory className="h-5 w-5" />
              工单详情
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {!isEditMode && (
                <div className="space-y-2">
                  <Label>工单号（可选）</Label>
                  <Input
                    value={productionOrderId}
                    onChange={(e) => setProductionOrderId(e.target.value)}
                    placeholder="留空则自动生成"
                  />
                </div>
              )}
              {isEditMode && productionOrderId && (
                <div className="space-y-2">
                  <Label>工单号</Label>
                  <Input 
                    value={productionOrderId} 
                    onChange={(e) => setProductionOrderId(e.target.value)}
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label>优先级</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">低</SelectItem>
                    <SelectItem value="normal">普通</SelectItem>
                    <SelectItem value="high">高</SelectItem>
                    <SelectItem value="urgent">紧急</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>工作中心</Label>
                <Select
                  value={workCenterId || 'none'}
                  onValueChange={(val) =>
                    setWorkCenterId(val === 'none' ? '' : val)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="选择工作中心..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">未分配</SelectItem>
                    {workCenters.map((wc) => (
                      <SelectItem key={wc._id} value={wc._id}>
                        {wc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>客户（用于库存归集）<span className="text-red-500">*</span></Label>
                <ClientSelector
                  selectedClientId={clientId}
                  selectedClientName={clientName}
                  onClientChange={(id, clientData) => {
                    setClientId(id);
                    setClientName(clientData?.name || '');
                  }}
                  showGlobalOption={false}
                  required={true}
                  placeholder="选择客户以正确归集库存"
                  helperText="选择客户后，生产产生的余料将归集到该客户名下"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  计划开始日期
                </Label>
                <Input
                  type="date"
                  value={plannedStartDate}
                  onChange={(e) => setPlannedStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  计划完成日期
                </Label>
                <Input
                  type="date"
                  value={plannedEndDate}
                  onChange={(e) => setPlannedEndDate(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Material Requirements Preview */}
        {materialRequirements.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                物料需求预览
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>产品</TableHead>
                    <TableHead>物料代码</TableHead>
                    <TableHead>物料名称</TableHead>
                    <TableHead className="text-right">单位用量</TableHead>
                    <TableHead className="text-right">损耗率</TableHead>
                    <TableHead className="text-right">需求量</TableHead>
                    <TableHead>单位</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {materialRequirements.map((material, index) => (
                    <TableRow key={index}>
                      <TableCell className="text-muted-foreground text-xs">
                        {material._item_info.product_code}
                      </TableCell>
                      <TableCell className="font-mono">
                        {material.material_product_code}
                      </TableCell>
                      <TableCell>{material.material_name}</TableCell>
                      <TableCell className="text-right">
                        {material.quantity_per}
                      </TableCell>
                      <TableCell className="text-right">
                        {((material.scrap_rate || 0) * 100).toFixed(1)}%
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {material.required_quantity}
                      </TableCell>
                      <TableCell>{material.unit}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Notes */}
        <Card>
          <CardHeader>
            <CardTitle>备注</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="可选生产工单备注..."
              rows={4}
            />
          </CardContent>
        </Card>
      </div>

      {/* BOM Search Dialog */}
      <Dialog open={bomSearchOpen} onOpenChange={setBomSearchOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>选择物料清单</DialogTitle>
            <DialogDescription>搜索并选择有效的BOM用于生产。</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="按BOM编号、产品代码或名称搜索..."
                value={bomSearchTerm}
                onChange={(e) => setBomSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="max-h-80 overflow-y-auto">
              {bomSearchLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : bomSearchResults.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  未找到有效的BOM。
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>BOM编号</TableHead>
                      <TableHead>产品</TableHead>
                      <TableHead>版本</TableHead>
                      <TableHead>物料数</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bomSearchResults.map((bom) => (
                      <TableRow
                        key={bom._id}
                        className="cursor-pointer hover:bg-muted"
                        onClick={() => selectBom(bom)}
                      >
                        <TableCell className="font-mono">
                          {bom.bom_id}
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {bom.finished_product_code}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {bom.finished_product_name}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>V{bom.version}</TableCell>
                        <TableCell>
                          {(bom.materials && bom.materials.length) || 0}项
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBomSearchOpen(false)}>
              取消
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Search Dialog */}
      <Dialog open={productSearchOpen} onOpenChange={setProductSearchOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>选择产品</DialogTitle>
            <DialogDescription>搜索并选择产品，BOM可在后续补充。</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="按产品编号、名称或规格搜索..."
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="max-h-80 overflow-y-auto">
              {productSearchLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : productSearchResults.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  未找到产品。
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>产品编号</TableHead>
                      <TableHead>名称</TableHead>
                      <TableHead>规格</TableHead>
                      <TableHead>分类</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {productSearchResults.map((product) => (
                      <TableRow
                        key={product._id}
                        className="cursor-pointer hover:bg-muted"
                        onClick={() => selectProduct(product)}
                      >
                        <TableCell className="font-mono">
                          {product.product_id}
                        </TableCell>
                        <TableCell className="font-medium">
                          {product.name}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {product.specification}
                        </TableCell>
                        <TableCell>
                          <span className="text-xs bg-muted px-2 py-1 rounded">
                            {product.category}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setProductSearchOpen(false)}>
              取消
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={bomCreateOpen} onOpenChange={setBomCreateOpen}>
        <SheetContent side="right" className="w-[70vw] max-w-4xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>创建BOM</SheetTitle>
            <SheetDescription>
              创建新的物料清单，保存后将自动添加到当前生产工单。
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            <BomForm
              onSuccess={handleBomCreated}
              onCancel={() => setBomCreateOpen(false)}
            />
          </div>
        </SheetContent>
      </Sheet>
    </Layout>
  );
}

export default ProductionFormPage;
